import { useState } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Clock, Play, Plus } from "lucide-react";
import type { Task } from "@/types";
import { formatTime, getElapsedSeconds } from "@/utils/helpers";

interface OvertimeDialogProps {
    task: Task;
    isOpen: boolean;
    onContinue: () => void;
    onAddTime: (minutes: number) => void;
    onComplete: () => void;
}

export function OvertimeDialog({
    task,
    isOpen,
    onContinue,
    onAddTime,
    onComplete,
}: OvertimeDialogProps) {
    const [customMinutes, setCustomMinutes] = useState("");

    if (!isOpen) return null;

    const totalElapsed = getElapsedSeconds(task);
    const overtimeSeconds = totalElapsed - task.estimatedMinutes * 60;
    const quickAddOptions = [5, 10, 15, 30];

    const handleAddCustomTime = () => {
        const mins = parseInt(customMinutes);
        if (mins > 0) {
            onAddTime(mins);
            setCustomMinutes("");
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            {/* Modal */}
            <Card className="relative z-10 w-full max-w-md border-0 shadow-2xl">
                <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                            <AlertTriangle className="w-6 h-6 text-red-500" />
                        </div>
                        <div>
                            <CardTitle className="text-lg">
                                Task Overtime!
                            </CardTitle>
                            <CardDescription>
                                Your task went over the estimated time
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Task Info */}
                    <div className="p-4 bg-muted/50 rounded-lg">
                        <div className="font-medium mb-2">{task.name}</div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                <span>Estimated: {task.estimatedMinutes}m</span>
                            </div>
                            <div className="flex items-center gap-1 text-red-500">
                                <AlertTriangle className="w-4 h-4" />
                                <span>
                                    Overtime: {formatTime(overtimeSeconds)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Options */}
                    <div className="space-y-3">
                        <p className="text-sm font-medium">
                            What would you like to do?
                        </p>

                        {/* Continue Button */}
                        <Button
                            onClick={onContinue}
                            variant="outline"
                            className="w-full justify-start gap-2"
                        >
                            <Play className="w-4 h-4" />
                            Continue working on this task
                        </Button>

                        {/* Add Time Options */}
                        <div className="space-y-2">
                            <p className="text-xs text-muted-foreground">
                                Add more time:
                            </p>
                            <div className="flex gap-2 flex-wrap">
                                {quickAddOptions.map((mins) => (
                                    <Button
                                        key={mins}
                                        size="sm"
                                        variant="secondary"
                                        onClick={() => onAddTime(mins)}
                                    >
                                        +{mins}m
                                    </Button>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Input
                                        type="number"
                                        placeholder="Custom"
                                        value={customMinutes}
                                        onChange={(e) =>
                                            setCustomMinutes(e.target.value)
                                        }
                                        className="pr-12"
                                        min="1"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                                        min
                                    </span>
                                </div>
                                <Button
                                    onClick={handleAddCustomTime}
                                    disabled={
                                        !customMinutes ||
                                        parseInt(customMinutes) <= 0
                                    }
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add
                                </Button>
                            </div>
                        </div>

                        {/* Complete Button */}
                        <Button
                            onClick={onComplete}
                            className="w-full bg-green-600 hover:bg-green-700"
                        >
                            Mark as Complete
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
